"use client";

import { useEffect } from "react";
import Link from "@/components/LocaleLink";
import { AlertTriangle, ArrowRight, Home, RefreshCcw } from "lucide-react";
import { buttonVariants } from "@/components/ui/Button";
import { useTranslation } from "@/lib/i18n/LanguageContext";

// Only what is true for every error: no "logged" (only server errors are) and
// no "your data is safe" (form input on the page may just have been lost).
const COPY = {
  de: {
    title: "Da ist etwas",
    accent: "schiefgegangen.",
    lead: "Bitte versucht es gleich noch einmal oder schreibt uns direkt per WhatsApp.",
    reference: "Referenz:",
    retry: "Erneut versuchen",
    home: "Zur Startseite",
    contact: "Problem bleibt? Schreibt uns",
  },
  en: {
    title: "Something",
    accent: "went wrong.",
    lead: "Please try again in a moment or message us directly on WhatsApp.",
    reference: "Reference:",
    retry: "Try again",
    home: "Back to home",
    contact: "Still not working? Write to us",
  },
  da: {
    title: "Noget",
    accent: "gik galt.",
    lead: "Prøv venligst igen om et øjeblik, eller skriv direkte til os på WhatsApp.",
    reference: "Reference:",
    retry: "Prøv igen",
    home: "Til forsiden",
    contact: "Virker det stadig ikke? Skriv til os",
  },
  ar: {
    title: "حدث",
    accent: "خطأ ما.",
    lead: "يرجى المحاولة مرة أخرى بعد لحظات، أو مراسلتنا مباشرةً عبر WhatsApp.",
    reference: "المرجع:",
    retry: "إعادة المحاولة",
    home: "العودة إلى الصفحة الرئيسية",
    contact: "ما زالت المشكلة قائمة؟ راسلونا",
  },
  uk: {
    title: "Щось",
    accent: "пішло не так.",
    lead: "Спробуйте ще раз за мить або напишіть нам напряму у WhatsApp.",
    reference: "Ідентифікатор:",
    retry: "Спробувати ще раз",
    home: "На головну",
    contact: "Досі не працює? Напишіть нам",
  },
  ru: {
    title: "Что-то",
    accent: "пошло не так.",
    lead: "Попробуйте ещё раз чуть позже или напишите нам напрямую в WhatsApp.",
    reference: "Код ошибки:",
    retry: "Попробовать снова",
    home: "На главную",
    contact: "Всё ещё не работает? Напишите нам",
  },
  vi: {
    title: "Đã có",
    accent: "lỗi xảy ra.",
    lead: "Vui lòng thử lại sau giây lát hoặc nhắn trực tiếp cho chúng tôi qua WhatsApp.",
    reference: "Mã tham chiếu:",
    retry: "Thử lại",
    home: "Về trang chủ",
    contact: "Vẫn chưa được? Hãy nhắn cho chúng tôi",
  },
  es: {
    title: "Algo",
    accent: "salió mal.",
    lead: "Por favor, inténtenlo de nuevo en un momento o escríbannos directamente por WhatsApp.",
    reference: "Referencia:",
    retry: "Intentar de nuevo",
    home: "Ir a la página de inicio",
    contact: "¿Sigue el problema? Escríbannos",
  },
} as const;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { locale } = useTranslation();
  const c = COPY[locale];

  useEffect(() => {
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
      console.error("App error boundary:", error);
    }
  }, [error]);

  return (
    <main className="flex min-h-[80vh] items-center justify-center bg-cream-50 px-5 py-24">
      <div className="mx-auto w-full max-w-xl text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-coral-50 text-coral-600">
          <AlertTriangle className="h-6 w-6" strokeWidth={1.8} aria-hidden />
        </div>
        <h1 className="mt-6 text-balance font-display text-[1.75rem] font-semibold leading-[1.1] text-ink-900 sm:text-[2.05rem] lg:text-[2.3rem]">
          {c.title}{" "}
          <span className="font-normal italic text-coral-700">{c.accent}</span>
        </h1>
        <p className="mx-auto mt-4 max-w-md text-[15px] leading-[1.7] text-navy-600 sm:text-[16px]">
          {c.lead}
        </p>
        {error?.digest && (
          <p className="mt-4 text-xs text-navy-400">
            {c.reference} <code className="font-mono">{error.digest}</code>
          </p>
        )}
        {/* One red action (try again), one white pill; contact as a text link. */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => reset()}
            className={buttonVariants({ variant: "primary", size: "md" })}
          >
            <RefreshCcw className="h-4 w-4" strokeWidth={2} aria-hidden />
            {c.retry}
          </button>
          <Link href="/" className={buttonVariants({ variant: "secondary", size: "md" })}>
            <Home className="h-4 w-4" strokeWidth={1.8} aria-hidden />
            {c.home}
          </Link>
        </div>
        <Link
          href="/kontakt"
          className="group mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-coral-700 hover:text-coral-800"
        >
          {c.contact}
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
            strokeWidth={2}
            aria-hidden
          />
        </Link>
      </div>
    </main>
  );
}
